import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Tag, Toast } from "@aurem/ui";
import { Decimal } from "decimal.js";

import { useI18n } from "providers/I18nProvider";
import {
  CustomAttribute,
  EmployeeGender,
  EmployeeInput,
  EmploymentStatus,
  FormCategory,
  gql,
} from "types";
import { formatISODate } from "utils/helpers";

import { EmployeeFormSchema } from "../../types/employee-form-schema.types";
import { convertCustomAttributesToArray } from "../../utils/employee-form.utils";

const ActiveBadge = () => {
  const { t } = useI18n();
  return (
    <Tag
      color="success"
      label={t(
        "apps.admin.pages.companies.companyId.employees.employeeId.page.benefitStatus.active",
      )}
    />
  );
};

const InactiveBadge = () => {
  const { t } = useI18n();
  return (
    <Tag
      label={t(
        "apps.admin.pages.companies.companyId.employees.employeeId.page.benefitStatus.inactive",
      )}
    />
  );
};

const GET_EMPLOYEE_NAME = gql(/* GraphQL */ `
  query GetEmployeeName($employeeId: String!) {
    findEmployee(id: $employeeId) {
      firstName
      lastName
    }
  }
`);

const GET_EMPLOYEE_AND_FUNDS = gql(/* GraphQL */ `
  query GetEmployeeAndFunds($employeeId: String!) {
    findEmployee(id: $employeeId) {
      id
      companyId
      firstName
      lastName
      customEmployeeValues {
        id
        name
        value
      }
      jobTitle
      startDate
      endDate
      nationalId
      workEmail
      nationality
      gender
      dateOfBirth
      employmentStatus
      department
      branch
      level
      phoneNumber
      passportNumber
      address
      company {
        id
        customCompanyValues {
          id
          name
          value
        }
      }
      productAssignments {
        id
        startDate
        endDate
        productInstanceId
        productInstance {
          id
          name
          isActive
          product {
            id
            name
          }
        }
        holdingSnapshot {
          total
        }
      }
    }
  }
`);

const UPDATE_EMPLOYEE = gql(/* GraphQL */ `
  mutation UpdateEmployee(
    $companyId: String!
    $id: String!
    $employee: EmployeeInput!
    $category: String!
  ) {
    updateEmployee(
      companyId: $companyId
      id: $id
      employee: $employee
      category: $category
    ) {
      id
      firstName
      lastName
      jobTitle
      workEmail
      customEmployeeValues {
        name
        value
      }
      nationalId
      nationality
    }
  }
`);

export const useEmployeeName = (employeeId: string) => {
  const { data, loading, error } = useQuery(GET_EMPLOYEE_NAME, {
    variables: { employeeId },
  });

  const employee = data?.findEmployee;

  return {
    firstName: employee?.firstName || "",
    lastName: employee?.lastName || "",
    loading,
    error,
  };
};

export const useEmployee = (
  employeeId: string,
  options?: { skip?: boolean },
) => {
  const { data, loading, error } = useQuery(GET_EMPLOYEE_AND_FUNDS, {
    variables: { employeeId },
    skip: options?.skip,
  });

  return {
    employee: data?.findEmployee,
    loading,
    error,
  };
};

export const useEditEmployee = (companyId: string, employeeId: string) => {
  const { t } = useI18n();

  const [updateEmployee, { error, loading }] = useMutation(UPDATE_EMPLOYEE, {
    refetchQueries: [GET_EMPLOYEE_AND_FUNDS],
  });

  const editEmployee = async (
    values: EmployeeFormSchema,
    customAttributes: CustomAttribute[],
    convertToBaseCurrencyDecimal: (
      value: string | number | Decimal,
    ) => Decimal | null,
    category: FormCategory,
  ) => {
    const employee: EmployeeInput = {
      firstName: values.firstName,
      lastName: values.lastName,
      nationalId: values.nationalId,
      gender: values.gender as EmployeeGender,
      nationality: values.nationality,
      passportNumber: values.passportNumber,
      startDate: formatISODate(values.startDate),
      endDate: formatISODate(values.endDate),
      dateOfBirth: formatISODate(values.dateOfBirth),
      customAttributes: convertCustomAttributesToArray(
        values.customAttributes || {},
        customAttributes,
        convertToBaseCurrencyDecimal,
      ),
      employmentStatus: values.employmentStatus as EmploymentStatus,
      phoneNumber: values.phoneNumber,
      workEmail: values.workEmail,
      address: values.address,
      jobTitle: values.jobTitle,
      branch: values.branch,
      department: values.department,
      level: values.level,
    };

    try {
      const result = await updateEmployee({
        variables: {
          companyId,
          id: employeeId,
          category,
          employee,
        },
        optimisticResponse: {
          updateEmployee: {
            __typename: "CrmEmployee",
            id: employeeId,
            firstName: employee.firstName,
            lastName: employee.lastName,
            jobTitle: employee.jobTitle ?? null,
            workEmail: employee.workEmail ?? null,
            nationalId: employee.nationalId,
            nationality: employee.nationality ?? null,
            customEmployeeValues: [],
          },
        },
        update: (cache) => {
          const existing = cache.readQuery({
            query: GET_EMPLOYEE_AND_FUNDS,
            variables: { employeeId },
          });
          if (!existing?.findEmployee) return;

          cache.writeQuery({
            query: GET_EMPLOYEE_AND_FUNDS,
            variables: { employeeId },
            data: {
              findEmployee: {
                ...existing.findEmployee,
                firstName: employee.firstName,
                lastName: employee.lastName,
                nationalId: employee.nationalId,
                nationality: employee.nationality ?? null,
                gender: employee.gender ?? null,
                dateOfBirth: employee.dateOfBirth ?? null,
                passportNumber: employee.passportNumber ?? null,
                startDate:
                  employee.startDate ?? existing.findEmployee.startDate,
                endDate: employee.endDate ?? null,
                employmentStatus: employee.employmentStatus ?? null,
                jobTitle: employee.jobTitle ?? null,
                department: employee.department ?? null,
                branch: employee.branch ?? null,
                level: employee.level ?? null,
                phoneNumber: employee.phoneNumber ?? null,
                address: employee.address ?? null,
                workEmail: employee.workEmail ?? null,
                customEmployeeValues: (employee.customAttributes ?? []).map(
                  (attr) => {
                    const previous =
                      existing.findEmployee.customEmployeeValues.find(
                        (value) => value.name === attr.name,
                      );
                    return {
                      __typename: "CustomEmployeeValue" as const,
                      id: previous?.id ?? `temp-cev-${employeeId}-${attr.name}`,
                      name: attr.name,
                      value: attr.value ?? null,
                    };
                  },
                ),
              },
            },
          });
        },
      });
      return !!result.data?.updateEmployee;
    } catch (e) {
      Toast.error({
        title:
          (e as ApolloError)?.message ||
          t(
            "apps.admin.pages.companies.companyId.employees.employeeId.page.error",
          ),
      });
      return false;
    }
  };

  return { editEmployee, loading, error };
};
